/* eslint-disable react-hooks/exhaustive-deps */
import {
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import FileUploader from '../../../shared/components/FileUploader';
import { ContentResponse, Folder, SubCategoryType } from '../../../../data/types/content.interface';
import { capitalize, isEmpty, isNull } from 'lodash';
import Toaster from '../../../shared/components/Toaster';
import { CloseIcon } from '../../../shared/components/AppIcons';
import ContentsService from '../../services/contents.services';
import LoadingBackdrop from '../../../shared/components/LoadingBackdrop';
import { TagType } from '../../../../data/types/tags.interface';

const useStyles = makeStyles({
  paper: {
    minWidth: '500px',
    height: 'auto',
    borderRadius: '21px',
    backgroundColor: '#F5F5F5 !important',
    boxShadow: 'rgba(242, 242, 242, 1)',
  },
  header: {
    fontSize: '24px',
  },
  close: {
    fontSize: '50px !important',
    '& path': {
      fill: `#2C2C2C !important`,
    },
  },
  save: {
    width: '122px',
    height: '38px',
    backgroundColor: '#F67570 !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
  },
  cancel: {
    color: '#F67570 !important',
    marginRight: '40px !important',
  },
});

const fileType = 'final';

interface Props {
  setOpenAddModal: Dispatch<SetStateAction<boolean>>;
  setAssetId: Dispatch<SetStateAction<number | null>>;
  setRefresh: Dispatch<SetStateAction<boolean>>;
  folderNames: Folder[];
  subCategories: SubCategoryType[];
  contentTypes: TagType[];
}

const AddFinalContent: React.FC<Props> = ({
  setOpenAddModal,
  setAssetId,
  setRefresh,
  folderNames,
  subCategories,
  contentTypes,
}) => {
  const classes = useStyles();
  const {
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: 'all' });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [contents, setContents] = useState<ContentResponse[]>([]);
  const [selectedContent, setSelectedContent] = useState<ContentResponse>();
  const [bulkUpload, setBulkUpload] = useState<boolean>(false);
  const [selectedAsset, setSelectedAsset] = useState<TagType | null>(null);
  const [videoType, setVideoType] = useState('short');
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategoryType | null>(null);
  const [folderInputValue, setFolderInputValue] = useState<string>('');

  const getToaster = () => {
    return <Toaster show={show} setShow={setShow} message="Content uploaded successfully" />;
  };

  const getRemovedContents = async () => {
    const contents = await ContentsService.getAllContents({
      type: fileType,
      isRemoved: true,
      perPage: 100,
      page: 1,
    });
    if (contents) {
      setContents(contents.rows);
    }
  };

  useEffect(() => {
    getRemovedContents();
  }, []);

  const onSubmit = async () => {
    try {
      let content;
      setIsLoading(true);

      if (!isEmpty(files) || videoFile) {
        const formData = new FormData();
        const config = { headers: { 'content-type': 'multipart/form-data' } };

        if (bulkUpload && files.length) {
          files.forEach((file) => formData.append('videos', file));
        } else if (videoFile) {
          formData.append('video', videoFile);
        }

        formData.append('path', selectedFolder?.path_display || folderInputValue);
        formData.append('subCategory', selectedSubCategory?.name || '');
        formData.append('type', 'final');

        content = bulkUpload
          ? await ContentsService.createFilesInBulk(formData, 'final', videoType, config)
          : await ContentsService.createContent(formData, 'final', videoType, config);
      } else if (selectedContent) {
        content = await ContentsService.updateContentDetails(selectedContent?.id, fileType, {
          isRemoved: false,
        });
      }

      if (content) {
        setShow(true);
        setAssetId(content.id);
        setRefresh(true);
        setOpenAddModal(false);
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.[0]?.message || 'Please try again');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (files: File[]) => {
    if (files.length > 0) {
      bulkUpload ? setFiles(files) : setVideoFile(files[0]);
    } else {
      bulkUpload ? setFiles([]) : setVideoFile(files[0]);
    }
  };

  const handleChange = (e: React.SyntheticEvent<Element, Event>, value: any | null) => {
    if (!isNull(value)) {
      setSelectedContent(value);
    }
  };

  const handleFolderChange = (e: React.SyntheticEvent<Element, Event>, value: any | null) => {
    if (!isNull(value)) {
      setSelectedFolder(value);
    }
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    setFolderInputValue(newInputValue);
  };

  const handleAssetChange = (e: React.SyntheticEvent<Element, Event>, value: TagType | null) => {
    if (isNull(value)) {
      setErrorMessage('Please select any one asset');
    } else {
      setSelectedAsset(value);
      setErrorMessage('');
    }
  };

  const handleVideoTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVideoType((event.target as HTMLInputElement).value);
  };

  return (
    <Dialog
      open
      aria-modal="true"
      role="dialog"
      onClose={() => setOpenAddModal(false)}
      PaperProps={{
        onClick: (e: any) => e.stopPropagation(),
        className: classes.paper,
      }}
    >
      <DialogTitle>
        <Grid2 container justifyContent="space-between" alignItems="center">
          <Grid2>
            <span className={classes.header}>Add Final File</span>
          </Grid2>
          <Grid2 container justifyContent="flex-end">
            <IconButton onClick={() => setOpenAddModal(false)} role="button" sx={{ padding: 0 }}>
              <CloseIcon viewBox="0 0 42 42" className={classes.close} />
            </IconButton>
          </Grid2>
        </Grid2>
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: '0px' }}>
        {getToaster()}
        <form onSubmit={handleSubmit(onSubmit)}>
          {errorMessage && errorMessage.length > 0 && (
            <Grid2 container justifyContent="center">
              <div className="text-danger mb-1 pb-5 text-center">{errorMessage}</div>
            </Grid2>
          )}

          <Grid2 container justifyContent="flex-end">
            <Grid2>
              <span>Bulk Upload</span>
            </Grid2>
            <Grid2 ml={1}>
              <input
                type="checkbox"
                checked={bulkUpload}
                className="checkbox"
                name="bulk-upload"
                onChange={() => {
                  setBulkUpload(!bulkUpload);
                }}
              />
            </Grid2>
          </Grid2>

          <Grid2 container direction="column" alignItems="center">
            <FileUploader
              handleFileUpload={(file: File[]) => handleFileUpload(file)}
              setErrorMessage={setErrorMessage}
              id={selectedAsset?.name}
              bulkUpload={bulkUpload}
            />
          </Grid2>

          <Grid2 container direction="column" ml={0}>
            <label>Asset Type*</label>
            <Stack spacing={1}>
              <Autocomplete
                options={contentTypes}
                getOptionLabel={(option: TagType) => capitalize(option.name)}
                id="auto-highlight"
                value={selectedAsset}
                autoHighlight
                onChange={handleAssetChange}
                className={`${errors.assetType && 'is-invalid'}`}
                renderInput={(params) => <TextField {...params} variant="standard" />}
              />
            </Stack>
            <>
              {selectedAsset?.name === 'video' && (
                <FormControl>
                  <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="controlled-radio-buttons-group"
                    value={videoType}
                    onChange={handleVideoTypeChange}
                  >
                    <Tooltip title="Original video clip">
                      <FormControlLabel value="long_original" control={<Radio />} label="Long Original" />
                    </Tooltip>
                    <Tooltip title="Short videos made out of original clip - Greater than 1 Min, but a chopped version, not full original file">
                      <FormControlLabel value="long_chopped" control={<Radio />} label="Long Chopped" />
                    </Tooltip>
                    <Tooltip title="Less than 1Min">
                      <FormControlLabel value="short" control={<Radio />} label="Short" />
                    </Tooltip>
                  </RadioGroup>
                </FormControl>
              )}
            </>
          </Grid2>

          <Grid2 container mt={1} direction="column">
            <Grid2>
              <label>Destination Path</label>
            </Grid2>
            <Grid2>
              <Stack spacing={1}>
                <Autocomplete
                  freeSolo
                  options={(folderNames as Folder[]) || []}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                  id="auto-highlight"
                  value={selectedFolder}
                  autoHighlight
                  onChange={handleFolderChange}
                  onInputChange={(event, newInputValue) => {
                    setFolderInputValue(newInputValue);
                    handleInputChange?.(event, newInputValue);
                  }}
                  renderInput={(params) => <TextField {...params} variant="standard" />}
                />
              </Stack>
            </Grid2>
            <Grid2>
              <label>Sub Category</label>{' '}
              <Stack spacing={1}>
                <Autocomplete
                  options={subCategories || []}
                  getOptionLabel={(option) => option.name}
                  id="auto-highlight"
                  value={selectedSubCategory}
                  autoHighlight
                  onChange={(e, value) => setSelectedSubCategory(value)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select any sub category" variant="standard" />
                  )}
                />
              </Stack>
            </Grid2>
          </Grid2>

          <Grid2 container mt={1} direction="column">
            <Grid2>
              <label>Removed Contents</label>
            </Grid2>
            <Grid2>
              <Stack spacing={1}>
                <Autocomplete
                  disabled={!isNull(videoFile) || !isEmpty(files) ? true : false}
                  options={contents || []}
                  getOptionLabel={(option) => option.filename}
                  id="auto-highlight"
                  value={selectedContent}
                  autoHighlight
                  onChange={handleChange}
                  renderInput={(params) => <TextField {...params} variant="standard" />}
                />
              </Stack>
            </Grid2>
          </Grid2>

          <Grid2 container justifyContent="center" m={2}>
            <Button
              size="small"
              variant="text"
              type="button"
              onClick={() => setOpenAddModal(false)}
              className={classes.cancel}
            >
              cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              className={classes.save}
              type="submit"
              disabled={(!(videoFile || files) && !selectedContent) || !isEmpty(errorMessage)}
            >
              <div className="caption" style={{ fontSize: 'h5', textTransform: 'none' }}>
                Save
              </div>
            </Button>
          </Grid2>
        </form>
        <LoadingBackdrop openBackdrop={isLoading} />
      </DialogContent>
    </Dialog>
  );
};

export default AddFinalContent;
